import { VERSION_TAG_LABEL } from '@/lib/versioned-labels';
import { AMBIANCE_ASSET_RULES } from '@matter_extractor/shared';
import type {
  AmbianceAsset,
  AmbianceAssetKind,
  AmbianceAssetReferent,
} from '@matter_extractor/shared';
import { assetDuration } from './ambiance-labels';
import { ApiError } from './api';

/**
 * The words of the Médiathèque screen (refonte modulaire §16, H7; issue #121).
 *
 * Kept apart from `ambiance-labels.ts`, which speaks for `media-picker.tsx`
 * and stops at what a PICKER needs (the accept list, one combined subtitle
 * line). This screen shows dimensions/duration, license and tags as three
 * SEPARATE facts rather than one folded line - a card is read at rest, not
 * chosen in a hurry - so it needs its own, finer-grained words.
 */

export const ASSET_KIND_LABEL: Readonly<Record<AmbianceAssetKind, string>> = {
  image: 'Image',
  sound: 'Son',
};

/**
 * `1920 x 1080` for an image, `1:24` for a sound, `null` when the header
 * carried neither - the field the reader could not read is OMITTED rather
 * than shown as a dash (same rule as `assetSubtitle`).
 */
export function assetDimensionsOrDuration(asset: AmbianceAsset): string | null {
  if (asset.width !== null && asset.height !== null) return `${asset.width} x ${asset.height}`;
  if (asset.durationMs !== null) return assetDuration(asset.durationMs);
  return null;
}

/**
 * « Accueil PMS (production, développement) » - one referent of a 409
 * `ASSET_IN_USE` (§9.1), worded from its tags rather than left as a raw
 * array a screen would have to translate itself.
 */
export function referentLine(referent: AmbianceAssetReferent): string {
  const tags = referent.tags.map((tag) => VERSION_TAG_LABEL[tag]).join(', ');
  return `${referent.name} (${tags})`;
}

/**
 * The French of an upload refusal, worded from `ApiError.code` - the upload
 * route answers the GENERIC envelope (`routes/ambiance.ts` throws a plain
 * `HttpError`, never a `BusinessRefusal`), so the code sits in `.code` and
 * not in `.details` the way `ASSET_IN_USE`'s does. `null` on anything else: a
 * transport failure or an unrecognised code falls back to the caller's own
 * generic sentence.
 *
 * `kind` is the nature of the file the operator last tried to upload - the
 * caller reads it off the picked `File`'s name (`ambianceFormatOf` plus
 * `AMBIANCE_KIND_OF_FORMAT`) before the request goes out. `FILE_TOO_LARGE`
 * needs it: `AMBIANCE_ASSET_RULES` keeps a ceiling PER kind, and a sentence
 * that always quoted the image's would silently answer wrong the day a
 * sound's `maxLabel` diverges from it. `null` when the kind could not be
 * read (an unrecognised extension, or no attempt yet) falls back on the
 * image ceiling, same as before this parameter existed.
 */
export function ambianceUploadRefusal(
  error: unknown,
  kind: AmbianceAssetKind | null = null,
): string | null {
  if (!(error instanceof ApiError)) return null;
  switch (error.code) {
    case 'FILE_REQUIRED':
      return 'Choisissez un fichier avant de téléverser.';
    case 'UNSUPPORTED_FORMAT':
      return 'Ce format n’est pas accepté : mp3 ou ogg pour un son, webp, jpg, png, gif ou avif pour une image.';
    case 'FILE_TOO_LARGE':
      return `Ce fichier dépasse ${AMBIANCE_ASSET_RULES[kind ?? 'image'].maxLabel} : compressez-le avant de le téléverser.`;
    default:
      return null;
  }
}
